/**
 * Immich サムネイル表示コンポーネント
 *
 * バックエンド /immich/thumbnail/{id} は JWT 認証が必要なため、
 * `<img src>` に直接指定できず、apiClient (Bearer 付き) で fetch して
 * Blob URL に変換する。IntersectionObserver で lazy 化する。
 */

import { useEffect, useMemo, useRef, useState } from 'react'

import { apiClient } from '@/api/client'
import { cn } from '@/lib/utils'

type Props = {
  assetId: string
  alt: string
  size?: 'thumbnail' | 'preview'
  className?: string
  onClick?: () => void
}

export const ImmichThumbnail = ({ assetId, alt, size = 'preview', className, onClick }: Props) => {
  const ref = useRef<HTMLDivElement>(null)
  const [isInView, setIsInView] = useState(false)
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  const path = useMemo(() => `/immich/thumbnail/${assetId}?size=${size}`, [assetId, size])

  // Lazy: ビューポートに入ったら読み込み開始
  useEffect(() => {
    if (isInView || !ref.current) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsInView(true)
          observer.disconnect()
        }
      },
      { rootMargin: '200px' },
    )
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [isInView])

  // 画像取得
  // NOTE: 生成した ObjectURL の解放は下の [objectUrl] cleanup effect に一元化する。
  //       ここで revoke すると、path 更新時に「旧 URL revoke → 新 URL 到着待ち」
  //       の間に <img src> が無効化されて画像が壊れる + 二重 revoke の要因になる。
  useEffect(() => {
    if (!isInView) return
    let cancelled = false

    apiClient
      .get<Blob>(path, { responseType: 'blob' })
      .then((res) => {
        // アンマウント後は URL を生成しない (revoke されないメモリリーク回避)
        if (cancelled) return
        setObjectUrl(URL.createObjectURL(res.data))
      })
      .catch(() => {
        if (cancelled) return
        setFailed(true)
      })

    return () => {
      cancelled = true
    }
  }, [isInView, path])

  // ObjectURL の解放 (path 変更でリセットされる旧 URL / アンマウント時の URL)
  useEffect(() => {
    if (!objectUrl) return
    return () => {
      URL.revokeObjectURL(objectUrl)
    }
  }, [objectUrl])

  if (!isInView) {
    return <div ref={ref} className={cn('bg-muted', className)} />
  }

  if (failed) {
    return (
      <div className={cn('bg-muted flex items-center justify-center text-xs text-muted-foreground', className)}>
        読込失敗
      </div>
    )
  }

  if (!objectUrl) {
    return <div className={cn('bg-muted animate-pulse', className)} />
  }

  return (
    <img
      src={objectUrl}
      alt={alt}
      className={cn('object-cover', className)}
      loading="lazy"
      decoding="async"
      onClick={onClick}
    />
  )
}
